import {
  Security as SecurityIcon,
  Policy as PolicyIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';

/**
 * Get policy icon based on policy name
 * @param {string} policyName - Policy name
 * @returns {React.ReactNode} Policy icon component
 */
export const getPolicyIcon = policyName => {
  switch (policyName) {
    case 'pii-policy':
      return <SecurityIcon color="primary" />;
    case 'hipaa-policy':
      return <ShieldIcon color="success" />;
    case 'financial-policy':
      return <PolicyIcon color="warning" />;
    default:
      return <PolicyIcon />;
  }
};

/**
 * Get compliance level information for a policy
 * @param {string} policyName - Policy name
 * @returns {Object} Compliance level object with level, color, and description
 */
export const getComplianceLevel = policyName => {
  switch (policyName) {
    case 'pii-policy':
      return {
        level: 'High',
        color: 'error',
        description: 'Personal Identifiable Information protection',
      };
    case 'hipaa-policy':
      return {
        level: 'Critical',
        color: 'error',
        description: 'Healthcare data protection compliance',
      };
    case 'financial-policy':
      return {
        level: 'High',
        color: 'warning',
        description: 'Financial data protection and compliance',
      };
    default:
      return {
        level: 'Medium',
        color: 'default',
        description: 'General data protection',
      };
  }
};

/**
 * Get entity sensitivity level
 * @param {string} entity - Entity name
 * @returns {string} Sensitivity level (restricted, confidential, internal)
 */
export const getEntitySensitivityLevel = entity => {
  const highSensitivity = [
    'US_SSN',
    'CREDIT_CARD',
    'BANK_ACCOUNT',
    'NPI',
    'DEA_NUMBER',
    'MEDICAL_LICENSE',
  ];
  const mediumSensitivity = [
    'EMAIL_ADDRESS',
    'PHONE_NUMBER',
    'IP_ADDRESS',
    'API_KEY',
  ];

  if (highSensitivity.some(e => entity.includes(e))) return 'restricted';
  if (mediumSensitivity.some(e => entity.includes(e))) return 'confidential';
  return 'internal';
};

/**
 * Get entity data type
 * @param {string} entity - Entity name
 * @returns {string} Data type
 */
export const getEntityDataType = entity => {
  if (
    entity.includes('SSN') ||
    entity.includes('NPI') ||
    entity.includes('DEA')
  )
    return 'identifier';
  if (entity.includes('EMAIL') || entity.includes('PHONE')) return 'contact';
  if (entity.includes('CREDIT_CARD') || entity.includes('BANK'))
    return 'financial';
  if (entity.includes('DATE') || entity.includes('TIME')) return 'temporal';
  if (entity.includes('LOCATION') || entity.includes('ADDRESS'))
    return 'geographic';
  return 'pii';
};

/**
 * Get entity pattern (regex)
 * @param {string} entity - Entity name
 * @returns {string} Pattern string
 */
export const getEntityPattern = entity => {
  const patterns = {
    EMAIL_ADDRESS: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
    PHONE_NUMBER: '^[\\d\\s\\-\\(\\)]+$',
    US_SSN: '^\\d{3}-\\d{2}-\\d{4}$',
    CREDIT_CARD: '^\\d{4}[\\s-]?\\d{4}[\\s-]?\\d{4}[\\s-]?\\d{4}$',
    IP_ADDRESS: '^\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}$',
  };

  for (const [key, pattern] of Object.entries(patterns)) {
    if (entity.includes(key)) return pattern;
  }
  return 'N/A';
};

/**
 * Get entity description
 * @param {string} entity - Entity name
 * @returns {string} Description
 */
export const getEntityDescription = entity => {
  const descriptions = {
    PERSON: 'Person names and identities',
    EMAIL_ADDRESS: 'Email addresses',
    PHONE_NUMBER: 'Phone numbers',
    US_SSN: 'US Social Security Numbers',
    CREDIT_CARD: 'Credit card numbers',
    LOCATION: 'Geographic locations and addresses',
    DATE_TIME: 'Dates and timestamps',
    IP_ADDRESS: 'IP addresses',
    URL: 'Web URLs',
    NPI: 'National Provider Identifier',
    DEA_NUMBER: 'DEA registration numbers',
    MEDICAL_LICENSE: 'Medical license numbers',
    BANK_ACCOUNT: 'Bank account numbers',
    ROUTING_NUMBER: 'Bank routing numbers',
    IBAN_CODE: 'International Bank Account Numbers',
    CRYPTO: 'Cryptocurrency wallet addresses',
    API_KEY: 'API keys and tokens',
    AUTHENTICATION_TOKEN: 'Authentication tokens',
  };

  return descriptions[entity] || `${entity.replace(/_/g, ' ')} detection`;
};

/**
 * Get domain-specific test data
 * @param {string} domainName - Domain name
 * @returns {Object} Test data object with message, description, and expectedEntities
 */
export const getDomainSpecificTestData = domainName => {
  const testData = {
    pii: {
      message:
        'Patient: John Doe, SSN: 123-45-6789, Email: john.doe@hospital.com, Phone: (555) 123-4567, Credit Card: 4532-1234-5678-9012',
      description:
        'Personal Identifiable Information test data including SSN, email, phone, and credit card numbers.',
      expectedEntities: [
        'PERSON',
        'US_SSN',
        'EMAIL_ADDRESS',
        'PHONE_NUMBER',
        'CREDIT_CARD',
      ],
    },
    hipaa: {
      message:
        'Medical Record: Patient ID 789456, NPI: 1234567890, DEA: AB1234567, Medical License: MD-2023-001, Patient: Sarah Johnson, DOB: 1985-03-15',
      description:
        'HIPAA compliance test data including patient identifiers, medical licenses, and healthcare provider information.',
      expectedEntities: [
        'PATIENT_ID',
        'NPI',
        'DEA_NUMBER',
        'MEDICAL_LICENSE',
        'PERSON',
        'DATE_TIME',
      ],
    },
    financial: {
      message:
        'Bank Account: 1234567890, Routing: 021000021, IBAN: GB29 NWBK 6016 1331 9268 19, Credit Card: 4532-1234-5678-9012, SSN: 987-65-4321, Account Holder: Jane Smith',
      description:
        'Financial data test including bank accounts, routing numbers, IBAN codes, and credit card information.',
      expectedEntities: [
        'US_BANK_NUMBER',
        'ROUTING_NUMBER',
        'IBAN_CODE',
        'CREDIT_CARD',
        'US_SSN',
        'PERSON',
      ],
    },
    security: {
      message:
        'Security Alert: IP 192.168.1.100, URL: https://suspicious-site.com, Crypto Wallet: 1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa, API Key: sk-1234567890abcdef, Access Token: eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c',
      description:
        'Security threat test data including IP addresses, URLs, crypto wallets, API keys, and JWT tokens.',
      expectedEntities: [
        'IP_ADDRESS',
        'URL',
        'CRYPTO',
        'API_KEY',
        'AUTHENTICATION_TOKEN',
      ],
    },
    compliance: {
      message:
        'Compliance Report: Organization: Acme Corp, Location: 123 Main St, New York, Date: 2024-01-15, Officer: Compliance Officer, Framework: SOX, Deadline: 2024-03-31',
      description:
        'Compliance and regulatory test data including organizations, locations, dates, and compliance officers.',
      expectedEntities: ['ORGANIZATION', 'LOCATION', 'DATE_TIME', 'PERSON'],
    },
  };

  return testData[domainName] || testData.pii;
};
